import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MyAnalysesPageProps, ConversationGroup } from './types';
import { getThumbnailUrl, getChartUrl, getMapUrl, getAnimationUrl, getPreviewUrl, loadConversationMessages } from './functions';
import AnalysisThumbnail, { shortId } from '../AnalysisThumbnail';
import AnalysisDetails from '../AnalysisDetails';
import ConversationTimeline, { buildTimeline } from '../ConversationTimeline';
import Permalink from '../Permalink';
import { fetchMyAnalyses, fetchAllMyAnalyses, fetchAnalysis, setAnalysisShared, setConversationShared } from '../../utils/api';
import { GCP_BACKEND_URL, Analysis, ConversationContext } from '../../types';
import { parseMarkdownBold } from '../../utils/helpers';
import { PAGE_PATHS, analysisPath, conversationPath, parseViewerTab, ViewerTab } from '../../App';
import './styles.scss';

const MyAnalysesPage: React.FC<MyAnalysesPageProps> = ({ 
  user, 
  setShowAuthModal, 
  setAuthView, 
  setIsFullPageView 
}) => {
  const ITEMS_PER_PAGE = 12;
  const navigate = useNavigate();
  const { sessionId: routeSessionId, tab: routeTab, conversationId: routeConversationId } =
    useParams<{ sessionId?: string; tab?: string; conversationId?: string }>();
  // The tab lives in the URL (/my-analyses/<id>/charts) so each view is linkable.
  const activeDetailTab: ViewerTab = parseViewerTab(routeTab);
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [conversationGroups, setConversationGroups] = useState<ConversationGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedAnalysis, setSelectedAnalysis] = useState<Analysis | null>(null);
  const [selectedMissing, setSelectedMissing] = useState<boolean>(false);
  const [selectedConversation, setSelectedConversation] = useState<ConversationGroup | null>(null);
  const [viewerKey, setViewerKey] = useState<number>(0);
  // Conversation timeline to re-open when the user presses Back from an analysis
  // that was opened from inside a timeline. A ref (not state) so the URL effect
  // below doesn't need it as a dependency.
  const returnToConversation = useRef<ConversationGroup | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // URL is the source of truth for the open analysis: /my-analyses/<session_id>.
  useEffect(() => {
    let cancelled = false;
    if (!routeSessionId) {
      setSelectedAnalysis(null);
      setSelectedMissing(false);
      return;
    }
    if (!user) return; // auth modal is shown by the effect above; retry once logged in
    const local =
      analyses.find(a => a.session_id === routeSessionId) ||
      conversationGroups.flatMap(g => g.analyses).find(a => a.session_id === routeSessionId);
    if (local) {
      setSelectedAnalysis(local);
      setSelectedMissing(false);
      return;
    }
    fetchAnalysis(routeSessionId, user.id)
      .then(a => { if (!cancelled) { setSelectedAnalysis(a); setSelectedMissing(false); } })
      .catch(() => { if (!cancelled) { setSelectedAnalysis(null); setSelectedMissing(true); } });
    return () => { cancelled = true; };
  }, [routeSessionId, analyses, conversationGroups, user]);

  // /my-analyses/c/<conversation_id> opens that conversation's timeline.
  useEffect(() => {
    if (!routeConversationId) {
      setSelectedConversation(null);
      return;
    }
    if (!user) return;
    if (selectedConversation?.conversation_id === routeConversationId) return;
    // Coming back from an analysis opened inside this timeline: reuse it.
    const cached = returnToConversation.current;
    if (cached?.conversation_id === routeConversationId) {
      returnToConversation.current = null;
      setSelectedConversation(cached);
      return;
    }
    if (loading) return;
    const group = conversationGroups.find(g => g.conversation_id === routeConversationId);
    if (group) {
      handleViewTimeline(group);
    } else {
      // Not on this page of results — rebuild the group from the API.
      fetchAllMyAnalyses(user.id)
        .then(all => {
          const items = all
            .filter(a => a.conversation_id === routeConversationId)
            .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
          if (!items.length) { showToast('Conversation not found', 'error'); navigate(PAGE_PATHS.myanalyses, { replace: true }); return; }
          handleViewTimeline({
            conversation_id: routeConversationId,
            analyses: items,
            first_query: items[0].query,
            location: items[0].location,
            created_at: items[0].created_at,
            count: items.length,
          });
        })
        .catch(() => showToast('Failed to load conversation', 'error'));
    }
  }, [routeConversationId, user, loading, conversationGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAnalysis = (item: Analysis, tab: ViewerTab = 'map', fromConversation: ConversationGroup | null = null) => {
    returnToConversation.current = fromConversation;
    if (fromConversation) setSelectedConversation(null);
    setViewerKey(prev => prev + 1);
    navigate(analysisPath(item.session_id, 'myanalyses', tab));
  };
  const setTab = (tab: ViewerTab) => {
    if (!selectedAnalysis) return;
    setViewerKey(prev => prev + 1);
    navigate(analysisPath(selectedAnalysis.session_id, 'myanalyses', tab), { replace: true });
  };
  const closeAnalysis = () => {
    const back = returnToConversation.current;
    navigate(back ? conversationPath(back.conversation_id) : PAGE_PATHS.myanalyses);
  };
  const openConversation = (group: ConversationGroup) => navigate(conversationPath(group.conversation_id));
  const closeConversation = () => { returnToConversation.current = null; navigate(PAGE_PATHS.myanalyses); };

  const copyConversationLink = async (group: ConversationGroup) => {
    // Once shared, hand out the public community link so anyone can open it.
    const isPublic = group.analyses.length > 0 && group.analyses.every(a => a.is_shared);
    const url = `${window.location.origin}${conversationPath(group.conversation_id, isPublic ? 'community' : 'myanalyses')}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast(isPublic ? '🔗 Public link copied' : '🔗 Private link copied (only you can open it)');
    } catch {
      showToast(url, 'error');
    }
  };

  const copyLink = async (item: Analysis, tab?: ViewerTab) => {
    // Shared analyses get the public community link; private ones a link only the owner can open.
    const url = `${window.location.origin}${analysisPath(item.session_id, item.is_shared ? 'community' : 'myanalyses', tab)}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast(item.is_shared ? 'Public link copied' : 'Private link copied (only you can open it)');
    } catch {
      showToast(url, 'error');
    }
  };

  useEffect(() => {
    if (!user) {
      setShowAuthModal(true);
      setAuthView('signup');
      return;
    }
    loadMyAnalyses();
  }, [user, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsFullPageView(!!selectedAnalysis || !!selectedConversation);
  }, [selectedAnalysis, selectedConversation, setIsFullPageView]);

  const handleShareConversation = async (analyses: Analysis[]) => {
    try {
      const convId = analyses[0]?.conversation_id;
      if (convId) {
        const res = await setConversationShared(convId, user!.id, true);
        showToast(`✅ Shared ${res.updated} analyses with community!`);
      } else {
        await Promise.all(analyses.map(a => setAnalysisShared(a.session_id, user!.id, true)));
        showToast(`✅ Shared ${analyses.length} analyses with community!`);
      }
      loadMyAnalyses();
    } catch (err) {
      console.error('Failed to share conversation:', err);
      showToast('Failed to share conversation', 'error');
    }
  };

  const loadMyAnalyses = async () => {
    try {
      setLoading(true);
      console.log('🔍 LOG 100: Starting loadMyAnalyses...');
      console.log('🔍 LOG 101: User ID:', user!.id);

      const pageData = await fetchMyAnalyses(user!.id, currentPage, ITEMS_PER_PAGE);
      setTotalCount(pageData.total || 0);

      const allAnalyses: Analysis[] = pageData.items || [];
      console.log('🔍 LOG 103: Total analyses fetched:', allAnalyses.length);

      const conversations: Record<string, Analysis[]> = {};
      const standalone: Analysis[] = [];

      allAnalyses.forEach((analysis: Analysis) => {
        if (analysis.conversation_id) {
          if (!conversations[analysis.conversation_id]) {
            conversations[analysis.conversation_id] = [];
          }
          conversations[analysis.conversation_id].push(analysis);
        } else {
          standalone.push(analysis);
        }
      });

      Object.keys(conversations).forEach(convId => {
        conversations[convId].sort((a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });

      const groups: ConversationGroup[] = Object.entries(conversations).map(([convId, items]) => ({
        conversation_id: convId,
        analyses: items,
        first_query: items[0].query,
        location: items[0].location,
        created_at: items[0].created_at,
        count: items.length
      }));

      groups.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setConversationGroups(groups);
      setAnalyses(standalone);
    } catch (err) {
      console.error('🔍 LOG 115: ERROR in loadMyAnalyses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTimeline = async (conversationGroup: ConversationGroup) => {
    try {
      const messages = await loadConversationMessages(conversationGroup.conversation_id);
      setSelectedConversation({
        ...conversationGroup,
        timeline: buildTimeline(messages, conversationGroup.analyses),
      });
    } catch (err) {
      console.error('Failed to load timeline:', err);
      showToast('Failed to load conversation timeline', 'error');
    }
  };

  const handleShare = async (sessionId: string, currentShared: boolean) => {
    try {
      await setAnalysisShared(sessionId, user!.id, !currentShared);
      showToast(currentShared ? '🔒 Analysis is now private' : '✅ Shared with community!');
      loadMyAnalyses();
    } catch (err) {
      showToast('Failed to update sharing status', 'error');
    }
  };

  const handleDownload = async (sessionId: string) => {
    try {
      const response = await fetch(`${GCP_BACKEND_URL}/results/${sessionId}/download`);
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analysis_${sessionId}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      showToast('Failed to download results: ' + (err as Error).message, 'error');
    }
  };

  if (!user) {
    return null;
  }

  if (routeSessionId && selectedMissing) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>Analysis not found</h3>
          <p>This analysis doesn't exist or belongs to another account.</p>
          <p className="analysis-card-id" title={routeSessionId}>{routeSessionId}</p>
          <button onClick={closeAnalysis} className="btn btn-secondary">← Back to My Analyses</button>
        </div>
      </div>
    );
  }

  if (loading || (routeSessionId && !selectedAnalysis)) {
    return (
      <div className="page-container">
        <div className="loading-text">Loading your analyses...</div>
      </div>
    );
  }

  // Timeline View (shared component — identical to what Community visitors see)
  if (selectedConversation) {
    const allShared = selectedConversation.analyses.length > 0 && selectedConversation.analyses.every(a => a.is_shared);
    return (
      <ConversationTimeline
        conversation={selectedConversation}
        scope="myanalyses"
        userLabel="You"
        onBack={closeConversation}
        backLabel="← Back to List"
        onOpenAnalysis={(a, tab) => openAnalysis(a, tab, selectedConversation)}
        permalink={
          <Permalink
            path={conversationPath(selectedConversation.conversation_id, allShared ? 'community' : 'myanalyses')}
            isPublic={allShared}
          />
        }
        headerActions={
          <>
            <button onClick={() => copyConversationLink(selectedConversation)} className="btn btn-secondary" title="Copy link to this conversation">🔗 Copy link</button>
            <button
              onClick={() => handleShareConversation(selectedConversation.analyses)}
              className="btn btn-primary"
              disabled={allShared}
              title={allShared ? 'Already shared with the community' : 'Share this conversation with the community'}
            >
              {allShared ? 'Shared' : 'Share'}
            </button>
          </>
        }
      />
    );
  }

  // Individual Analysis View
  if (selectedAnalysis) {
    let conversationData: ConversationContext | null = null;
    if (selectedAnalysis.conversation_context) {
      try {
        conversationData = typeof selectedAnalysis.conversation_context === 'string' ? JSON.parse(selectedAnalysis.conversation_context) : selectedAnalysis.conversation_context;
      } catch (e) {
        console.error('Failed to parse conversation context:', e);
      }
    }

    return (
      <div className="page-container-full">
        <div className="viewer-header">
          <button onClick={closeAnalysis} className="btn btn-secondary">
            ← Back to List
          </button>
          <div className="viewer-header-info">
            <h2>{selectedAnalysis.query}</h2>
            <div className="viewer-conversation-info">
              <span className="analysis-card-id" title={selectedAnalysis.session_id}>{shortId(selectedAnalysis)}</span>
              {selectedAnalysis.conversation_id && (
                <> · 💬 Part of conversation {selectedAnalysis.conversation_id.slice(0, 8)}...</>
              )}
            </div>
          </div>
          <div className="viewer-tabs">
            {selectedAnalysis.analysis_type === 'video_animation' ? (
              <>
                <button onClick={() => setTab('map')} className={`viewer-tab ${activeDetailTab === 'map' ? 'active' : ''}`}>Animation</button>
                <button onClick={() => setTab('charts')} className={`viewer-tab ${activeDetailTab === 'charts' ? 'active' : ''}`}>Preview</button>
              </>
            ) : (
              <>
                <button onClick={() => setTab('map')} className={`viewer-tab ${activeDetailTab === 'map' ? 'active' : ''}`}>Map</button>
                <button onClick={() => setTab('charts')} className={`viewer-tab ${activeDetailTab === 'charts' ? 'active' : ''}`}>Charts</button>
              </>
            )}
            <button onClick={() => setTab('details')} className={`viewer-tab ${activeDetailTab === 'details' ? 'active' : ''}`}>Details</button>
          </div>
          <button onClick={() => copyLink(selectedAnalysis, activeDetailTab)} className="btn btn-secondary" title="Copy link to this view">🔗 Copy link</button>
          <button onClick={() => handleDownload(selectedAnalysis.session_id)} className="btn btn-primary">Download</button>
        </div>
        <Permalink
          path={analysisPath(selectedAnalysis.session_id, selectedAnalysis.is_shared ? 'community' : 'myanalyses', activeDetailTab)}
          isPublic={!!selectedAnalysis.is_shared}
        />

        {conversationData && conversationData.history && conversationData.history.length > 0 && (
          <div className="compact-conversation-history">
            <h4 className="compact-history-title">Conversation History:</h4>
            <div className="compact-history-scroll">
              {conversationData.history.map((item, index) => (
                <div key={index} className="compact-history-item">
                  <div className="compact-query-row">
                    <div className="compact-query-bubble">{item.query}</div>
                  </div>
                  <div className="compact-response-row">
                    <div className="compact-response-label">LLEO:</div>
                    <div className="compact-response-bubble">{parseMarkdownBold(item.response)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="full-viewer">
          {activeDetailTab === 'map' && (
            selectedAnalysis.analysis_type === 'video_animation' ? (
              <img key={viewerKey} src={getAnimationUrl(selectedAnalysis)} alt="Satellite Timelapse Animation" className="full-chart-img" style={{ maxWidth: '100%', borderRadius: '8px', objectFit: 'contain' }} />
            ) : (
              <iframe key={viewerKey} src={getMapUrl(selectedAnalysis)} className="full-iframe" title="Analysis Results" />
            )
          )}
          {activeDetailTab === 'charts' && (
            selectedAnalysis.analysis_type === 'video_animation' ? (
              <img key={viewerKey} src={getPreviewUrl(selectedAnalysis)} alt="Animation Preview Grid" className="full-chart-img" />
            ) : (
              <img key={viewerKey} src={getChartUrl(selectedAnalysis)} alt="Analysis Charts" className="full-chart-img" />
            )
          )}
          {activeDetailTab === 'details' && <AnalysisDetails analysis={selectedAnalysis} />}
        </div>
      </div>
    );
  }

  // Main Analysis Grid
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>My Analyses</h1>
        <p>Manage and view all your satellite analysis results</p>
      </div>

      {analyses.length === 0 && conversationGroups.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📂</div>
          <h3>No analyses yet</h3>
          <p>Start your first analysis from the home page!</p>
        </div>
      ) : (
        <div className="analysis-grid">
          {conversationGroups.map((group) => (
            <div
              key={`conv-${group.conversation_id}`}
              id={`conversation-${group.conversation_id}`}
              data-conversation-id={group.conversation_id}
              className="analysis-card conversation-card"
              onClick={() => openConversation(group)}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') openConversation(group); }}
            >
              <div className="card-badge conversation-badge">
                💬 {group.count} {group.count === 1 ? 'Message' : 'Messages'}
              </div>
              <AnalysisThumbnail item={group.analyses[0]} src={getThumbnailUrl(group.analyses[0])} alt="conversation" fallbackIcon="💬" />
              <div className="analysis-card-content">
                <h3 className="conversation-card-title">{group.first_query.length > 100 ? group.first_query.substring(0, 100) + '...' : group.first_query}</h3>
                <span className={`analysis-type-tag analysis-type-${group.analyses[0]?.analysis_type || 'unknown'}`}>
                  {(group.analyses[0]?.analysis_type || 'analysis').replace(/_/g, ' ')}
                </span>
                <p className="analysis-location">📍 {group.location}</p>
                <div className="analysis-meta-row">
                  <p className="analysis-date">{new Date(group.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                  <p className="analysis-card-id" title={`Conversation ID: ${group.conversation_id}`}>{shortId(group.analyses[0])}</p>
                </div>
                <div className="card-actions">
                  <a href={conversationPath(group.conversation_id)} onClick={(e) => { e.preventDefault(); e.stopPropagation(); openConversation(group); }} className="btn btn-primary btn-small flex-auto">Timeline</a>
                  <button onClick={(e) => { e.stopPropagation(); copyConversationLink(group); }} className="btn btn-secondary btn-small" title="Copy link to this conversation">🔗</button>
                  <button onClick={(e) => { e.stopPropagation(); handleShareConversation(group.analyses); }} className="btn btn-secondary btn-small">Share</button>
                </div>
              </div>
            </div>
          ))}

          {analyses.map((item) => (
            <div
              key={item.session_id || item.id}
              id={`analysis-${item.session_id || item.id}`}
              data-session-id={item.session_id}
              className={`analysis-card ${item.artifacts_expired ? 'analysis-card-expired' : ''}`}
              onClick={() => { if (!item.artifacts_expired) openAnalysis(item); }}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' && !item.artifacts_expired) openAnalysis(item); }}
            >
              <div className={`card-badge ${item.is_shared ? 'community-badge' : 'private-badge'}`}>{item.is_shared ? 'Shared' : 'Private'}</div>
              {item.conversation_id && <div className="card-badge conversation-badge-secondary">💬 Conversation</div>}
              <AnalysisThumbnail item={item} src={getThumbnailUrl(item)} />
              <div className="analysis-card-content">
                <h3>{item.analysis_type}</h3>
                <p className="analysis-query">{item.query}</p>
                <p className="analysis-location">📍 {item.location}</p>
                <div className="analysis-meta-row">
                  <p className="analysis-date">{new Date(item.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                  <p className="analysis-card-id" title={`Analysis ID: ${item.session_id}`}>{shortId(item)}</p>
                </div>
                {item.artifacts_expired && (
                  <p className="analysis-expired-note">Result files for this analysis are no longer available. Re-run the query to regenerate them.</p>
                )}
                <div className="card-actions">
                  <a
                    href={analysisPath(item.session_id, 'myanalyses')}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!item.artifacts_expired) openAnalysis(item); }}
                    className="btn btn-primary btn-small"
                    aria-disabled={!!item.artifacts_expired}
                    style={item.artifacts_expired ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
                  >
                    View Results
                  </a>
                  <button onClick={(e) => { e.stopPropagation(); copyLink(item); }} className="btn btn-secondary btn-small" title="Copy link to this analysis">🔗</button>
                  <button onClick={(e) => { e.stopPropagation(); handleShare(item.session_id, item.is_shared); }} className="btn btn-secondary btn-small" disabled={!!item.artifacts_expired}>{item.is_shared ? 'Make Private' : 'Share'}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalCount > ITEMS_PER_PAGE && (
        <div className="pagination-controls">
          <button
            onClick={() => { setCurrentPage(prev => Math.max(prev - 1, 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            disabled={currentPage === 1}
            className="btn btn-secondary btn-small"
          >
            ← Previous
          </button>
          <div className="pagination-pages">
            {Array.from({ length: Math.ceil(totalCount / ITEMS_PER_PAGE) }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`pagination-page-btn ${currentPage === page ? 'active' : ''}`}
              >
                {page}
              </button>
            ))}
          </div>
          <button
            onClick={() => { setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalCount / ITEMS_PER_PAGE))); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            disabled={currentPage >= Math.ceil(totalCount / ITEMS_PER_PAGE)}
            className="btn btn-secondary btn-small"
          >
            Next →
          </button>
        </div>
      )}

      {totalCount > 0 && (
        <div className="analysis-count-text">
          Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} analyses
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`toast-notification toast-${toast.type}`}>
          <span>{toast.message}</span>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default MyAnalysesPage;
